/**
 * AdditionalDestinationsPicker — Mail PR A FE
 *
 * Companion to <AttachmentSavePrompt /> (PR #413) and the backend
 * `additional_destinations` parameter shipped in PR #414.
 *
 * UX (director Vanessa, authoritative):
 *   - Attendances stays canonical (the backend pins this) — this picker
 *     ONLY governs the additional copies.
 *   - Matter top-level folders only.  No nesting, no firm-level folders,
 *     no auto-suggest.
 *   - Multi-select.  Zero or more — zero selections is a valid choice
 *     (means "no additional copies").
 *   - Cancel reverts to the prior selection (does not clobber it).
 *   - No silent defaults: if the folder list fails to load, surface the
 *     error via window.errorMessage(err, fallback) and keep the modal
 *     open so the user can retry.
 *
 * The component does NOT add a new backend call — callers pass in the
 * already-fetched `folders` list (matter top-level, from the same
 * /bonsai/mail/matters/{drive_id}/{item_id}/subfolders endpoint that
 * <AttachmentSavePrompt /> uses).  Single source of truth lives with
 * the caller.
 */
import { useState, useCallback, useEffect } from "react";
import { FolderOpen, Loader2, X, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { errorMessage } from "../utils/errorMessage";

// Vanessa's standing rule: never render [object Object], never stringify
// an Error verbatim, never leak the bare axios "Request failed with status
// code N". The canonical formatter enforces all three.
function _formatError(err, fallback) {
  if (typeof err === "string") return err || fallback;
  return errorMessage(err, fallback);
}

export default function AdditionalDestinationsPicker({
  isOpen,
  onClose,
  onConfirm,
  matterRef = "",
  currentSelection = [],
  folders = [],
  loadingFolders = false,
  foldersError = null,
  onLoadFolders, // optional () => Promise<void> — caller-supplied lazy loader
  emailCount = 1, // headline-only — bulk mode shows N emails wording
}) {
  const [selectedIds, setSelectedIds] = useState(currentSelection || []);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Reset internal state every time the picker is reopened so a stale
  // selection from a previous filing job doesn't leak across.
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(currentSelection || []);
      setSubmitting(false);
      setLocalError(null);
      // Lazy-load folders once when the picker opens.
      if (onLoadFolders) {
        Promise.resolve(onLoadFolders()).catch((err) => {
          setLocalError(_formatError(err, "Failed to load folders"));
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleFolder = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setLocalError(null);
    try {
      // Zero selections is a valid outcome (means "no copies").  We
      // emit the empty array honestly — caller wires
      // additional_destinations: [] into the request body.
      await onConfirm(selectedIds);
      onClose?.();
    } catch (err) {
      setLocalError(_formatError(err, "Failed to save additional destinations"));
      setSubmitting(false);
    }
  }, [onConfirm, onClose, selectedIds, submitting]);

  const handleCancel = useCallback(() => {
    // Cancel reverts to the prior selection — does NOT silently apply
    // the in-modal selection.  Caller decides whether to proceed with
    // the original selection (often empty) or abort outright.
    setSelectedIds(currentSelection || []);
    onClose?.();
  }, [onClose, currentSelection]);

  if (!isOpen) return null;

  const headline =
    emailCount > 1
      ? `Pick additional folders to copy ${emailCount} filed email${emailCount === 1 ? "" : "s"} into. Attendances stays canonical.`
      : `Pick additional folders to copy this filed email into. Attendances stays canonical.`;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && handleCancel()}>
      <DialogContent
        data-testid="additional-destinations-picker"
        className="sm:max-w-[480px]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Also file to additional folders?
          </DialogTitle>
          <DialogDescription>
            {headline}
            {matterRef ? ` Matter: ${matterRef}.` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-2 max-h-[320px] overflow-auto">
          {loadingFolders && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading folders…
            </div>
          )}
          {!loadingFolders && foldersError && (
            <div
              data-testid="additional-destinations-folders-error"
              className="text-sm text-red-600 py-2"
            >
              {_formatError(foldersError, "Failed to load folders")}
            </div>
          )}
          {!loadingFolders && !foldersError && folders.length === 0 && (
            <div className="text-sm text-slate-500 py-2">
              No top-level folders available for this matter.
            </div>
          )}
          {!loadingFolders &&
            !foldersError &&
            folders.length > 0 &&
            folders.map((f) => {
              const checked = selectedIds.includes(f.id);
              return (
                <label
                  key={f.id}
                  data-testid="additional-destinations-folder-option"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer ${
                    checked
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    name="additional-destinations-folder"
                    value={f.id}
                    checked={checked}
                    onChange={() => toggleFolder(f.id)}
                    data-testid={`additional-destinations-checkbox-${f.id}`}
                  />
                  <FolderOpen className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">{f.name}</span>
                </label>
              );
            })}
        </div>

        {localError && (
          <div
            data-testid="additional-destinations-picker-error"
            className="text-sm text-red-600"
          >
            {localError}
          </div>
        )}

        <div className="text-xs text-slate-500 px-1">
          {selectedIds.length === 0
            ? "No additional copies will be made."
            : `${selectedIds.length} additional ${
                selectedIds.length === 1 ? "copy" : "copies"
              } will be created after Attendances filing succeeds.`}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleCancel}
            disabled={submitting}
            data-testid="additional-destinations-cancel"
          >
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={submitting}
            data-testid="additional-destinations-save"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-1" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
